import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  Zap, Mail, Phone, Target, Building2, RotateCcw, Reply, HandMetal,
  PhoneCall, Heart, Sprout, TrendingUp, BarChart3, Activity, Clock,
  Sparkles, ArrowUpRight, ArrowDownRight, Minus,
} from 'lucide-react';

const ICON_MAP = { Zap, Mail, Phone, Target, Building2, RotateCcw, Reply, HandMetal, PhoneCall, Heart, Sprout };
const CHART_COLORS = ['hsl(204,94%,40%)', 'hsl(171,66%,35%)', 'hsl(38,92%,50%)', 'hsl(4,82%,55%)', 'hsl(262,60%,50%)'];
const OUTCOME_COLORS = { positive: '#22c55e', neutral: '#eab308', negative: '#ef4444' };

function StatCard({ label, value, sub, icon: Icon, trend }) {
  return (
    <Card className="border border-border/50" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <div className="h-9 w-9 rounded-lg bg-muted/50 flex items-center justify-center">
            {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-2 text-[11px] ${trend > 0 ? 'text-emerald-500' : trend < 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
            {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : trend < 0 ? <ArrowDownRight className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
            {trend > 0 ? '+' : ''}{trend}% vs baseline
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TemplatePerformanceCard({ template }) {
  const Icon = ICON_MAP[template.icon] || Sparkles;
  const stats = template.stats || {};
  const cat = template.category;

  return (
    <Card className="border border-border/50" data-testid={`analytics-card-${template.template_id}`}>
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-muted/50 flex items-center justify-center shrink-0">
            <Icon className="h-4 w-4 text-foreground" />
          </div>
          <div className="min-w-0">
            <CardTitle className="text-sm font-semibold truncate">{template.name}</CardTitle>
            <Badge variant="secondary" className="text-[9px] mt-0.5 border-0">
              {cat === 'voice_script' ? 'Voice' : cat === 'agent_preset' ? 'Agent' : cat}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        {cat === 'cadence' && <CadenceStats stats={stats} />}
        {cat === 'email' && <EmailStats stats={stats} />}
        {cat === 'voice_script' && <VoiceStats stats={stats} />}
        {cat === 'agent_preset' && <PresetStats stats={stats} />}
      </CardContent>
    </Card>
  );
}

function CadenceStats({ stats }) {
  const total = stats.total_cadences || 0;
  if (total === 0) return <EmptyStats label="No cadences generated with this template yet" />;
  const pieData = [
    { name: 'Completed', value: stats.completed || 0 },
    { name: 'Active', value: stats.active || 0 },
    { name: 'Other', value: Math.max(0, total - (stats.completed || 0) - (stats.active || 0)) },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <MiniStat label="Total" value={total} />
        <MiniStat label="Completed" value={stats.completed} />
        <MiniStat label="Completion" value={`${stats.completion_rate}%`} />
      </div>
      {pieData.length > 0 && (
        <div className="h-[120px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={30} outerRadius={48} dataKey="value" strokeWidth={1}>
                {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 11 }} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
        <Activity className="w-3 h-3" /> Avg progress: {stats.avg_progress_pct}%
      </div>
    </div>
  );
}

function EmailStats({ stats }) {
  const total = stats.total_emails || 0;
  if (total === 0) return <EmptyStats label="No emails generated with this template yet" />;
  const barData = [
    { name: 'Sent', value: stats.sent || 0 },
    { name: 'Opened', value: stats.opened || 0 },
    { name: 'Replied', value: stats.replied || 0 },
    { name: 'Clicked', value: stats.clicked || 0 },
  ];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <MiniStat label="Open Rate" value={`${stats.open_rate}%`} highlight={stats.open_rate > 30} />
        <MiniStat label="Reply Rate" value={`${stats.reply_rate}%`} highlight={stats.reply_rate > 10} />
        <MiniStat label="Click Rate" value={`${stats.click_rate}%`} highlight={stats.click_rate > 5} />
      </div>
      <div className="h-[120px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={barData} barSize={20}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 11 }} />
            <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] text-muted-foreground">{stats.sent} sent / {total} total generated</p>
    </div>
  );
}

function VoiceStats({ stats }) {
  const total = stats.total_calls || 0;
  if (total === 0) return <EmptyStats label="No calls made with this template yet" />;
  const outcomeData = [
    { name: 'Positive', value: stats.outcome_positive || 0 },
    { name: 'Neutral', value: stats.outcome_neutral || 0 },
    { name: 'Negative', value: stats.outcome_negative || 0 },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <MiniStat label="Total Calls" value={total} />
        <MiniStat label="Positive" value={`${stats.positive_rate}%`} highlight={stats.positive_rate > 50} />
        <MiniStat label="Avg Duration" value={`${Math.round((stats.avg_duration_seconds || 0) / 60)}m`} />
      </div>
      {outcomeData.length > 0 && (
        <div className="h-[120px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={outcomeData} cx="50%" cy="50%" innerRadius={30} outerRadius={48} dataKey="value" strokeWidth={1}>
                {outcomeData.map((entry) => (
                  <Cell key={entry.name} fill={OUTCOME_COLORS[entry.name.toLowerCase()] || CHART_COLORS[0]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 11 }} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function PresetStats({ stats }) {
  const total = stats.total_influenced || 0;
  if (total === 0) return <EmptyStats label="No actions influenced by this preset yet" />;
  const data = [
    { name: 'Cadences', value: stats.influenced_cadences || 0 },
    { name: 'Emails', value: stats.influenced_emails || 0 },
    { name: 'Calls', value: stats.influenced_calls || 0 },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <MiniStat label="Cadences" value={stats.influenced_cadences} />
        <MiniStat label="Emails" value={stats.influenced_emails} />
        <MiniStat label="Calls" value={stats.influenced_calls} />
      </div>
      {data.length > 0 && (
        <div className="h-[120px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} barSize={24}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
              <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 11 }} />
              <Bar dataKey="value" fill={CHART_COLORS[4]} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <p className="text-[10px] text-muted-foreground">Total influenced: {total} actions</p>
    </div>
  );
}

function MiniStat({ label, value, highlight }) {
  return (
    <div className="text-center">
      <p className={`text-base font-bold ${highlight ? 'text-emerald-500' : ''}`}>{value}</p>
      <p className="text-[9px] text-muted-foreground uppercase tracking-wider">{label}</p>
    </div>
  );
}

function EmptyStats({ label }) {
  return (
    <div className="flex flex-col items-center justify-center py-6 text-center">
      <Clock className="h-6 w-6 text-muted-foreground/30 mb-2" />
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className="text-[10px] text-muted-foreground/60 mt-0.5">Data will appear as the Brain uses this template</p>
    </div>
  );
}

export default function AITemplateAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const { data: result } = await api.get('/api/ai-templates/analytics');
      setData(result);
    } catch {
      setData({ templates: [], overview: {}, timeline: [] });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="analytics-loading">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="border border-border/50">
              <CardContent className="p-4"><div className="h-16 bg-muted animate-pulse rounded" /></CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;
  const { overview = {}, templates = [], timeline = [] } = data;
  const hasData = overview.total_template_uses > 0;

  return (
    <div className="space-y-6" data-testid="template-analytics">
      {/* Overview Stats */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="analytics-overview">
        <StatCard label="Template Uses" value={overview.total_template_uses} sub="total actions influenced" icon={TrendingUp} />
        <StatCard label="Cadences" value={overview.template_cadences} sub={`${overview.legacy_cadences} without template`} icon={Zap} />
        <StatCard label="Emails" value={overview.template_emails} sub={`${overview.legacy_emails} without template`} icon={Mail} />
        <StatCard label="Voice Calls" value={overview.template_calls} sub={`${overview.legacy_calls} without template`} icon={Phone} />
      </div>

      {/* Usage Timeline */}
      {timeline.length > 0 && (
        <Card className="border border-border/50" data-testid="analytics-timeline">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-muted-foreground" /> Template Usage Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                  <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 11 }} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                  <Area type="monotone" dataKey="cadences" stackId="1" fill={CHART_COLORS[0]} stroke={CHART_COLORS[0]} fillOpacity={0.4} />
                  <Area type="monotone" dataKey="emails" stackId="1" fill={CHART_COLORS[1]} stroke={CHART_COLORS[1]} fillOpacity={0.4} />
                  <Area type="monotone" dataKey="calls" stackId="1" fill={CHART_COLORS[2]} stroke={CHART_COLORS[2]} fillOpacity={0.4} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-Template Cards */}
      {!hasData && (
        <Card className="border border-border/50 border-dashed" data-testid="analytics-empty-state">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="h-14 w-14 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
              <BarChart3 className="h-7 w-7 text-muted-foreground/40" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">No template-influenced data yet</p>
            <p className="text-xs text-muted-foreground/60 mt-1 max-w-sm text-center">
              As the Brain generates cadences, emails, and call briefs using your installed templates, performance metrics will appear here with real data.
            </p>
          </CardContent>
        </Card>
      )}

      {templates.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Activity className="h-4 w-4 text-muted-foreground" /> Per-Template Performance
          </h3>
          <div className="grid gap-4 sm:grid-cols-2" data-testid="analytics-template-grid">
            {templates.map(t => (
              <TemplatePerformanceCard key={t.template_id} template={t} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
